/**
 * Tables and charts in answers (ADMIN-201): the pure part. The model's `show_visual` call
 * (packages/api/src/domains/chat/agent/visual-tools.ts) is the visual; this reads it defensively,
 * since a stored message may come from an older build, and formats, sorts and exports it.
 */

type VisualKind = "table" | "bar" | "line" | "pie";
type ColumnFormat = "text" | "number" | "currency" | "percent";
export type Cell = string | number | null;

export type VisualColumn = { label: string; format: ColumnFormat; unit?: string | undefined };

export type Visual = {
  kind: VisualKind;
  title?: string | undefined;
  columns: VisualColumn[];
  rows: Cell[][];
  note?: string | undefined;
};

const KINDS = new Set<VisualKind>(["table", "bar", "line", "pie"]);
const FORMATS = new Set<ColumnFormat>(["text", "number", "currency", "percent"]);

const text = (value: unknown) => (typeof value === "string" && value.trim() ? value : undefined);

/** The tool call's input as a visual, or null when it can't be drawn. */
export function readVisual(input: unknown): Visual | null {
  if (!input || typeof input !== "object") return null;
  const raw = input as Record<string, unknown>;
  const kind = raw.kind as VisualKind;
  if (!KINDS.has(kind) || !Array.isArray(raw.columns) || !Array.isArray(raw.rows)) return null;

  const columns = raw.columns.flatMap((c, i): VisualColumn[] => {
    if (!c || typeof c !== "object") return [];
    const col = c as Record<string, unknown>;
    const format = FORMATS.has(col.format as ColumnFormat)
      ? (col.format as ColumnFormat)
      : i === 0
        ? "text"
        : "number";
    return [{ label: text(col.label) ?? `Column ${i + 1}`, format, unit: text(col.unit) }];
  });
  if (columns.length < 2) return null;

  const rows = raw.rows.flatMap((r): Cell[][] =>
    Array.isArray(r)
      ? [
          columns.map((_, i) => {
            const v = r[i];
            return typeof v === "string" || (typeof v === "number" && Number.isFinite(v))
              ? v
              : null;
          }),
        ]
      : [],
  );
  if (rows.length === 0) return null;
  return { kind, title: text(raw.title), columns, rows, note: text(raw.note) };
}

const formatters = new Map<string, Intl.NumberFormat>();

function numberFormat(column: VisualColumn, compact: boolean): Intl.NumberFormat {
  const key = `${column.format}:${column.unit ?? ""}:${compact}`;
  let format = formatters.get(key);
  if (!format) {
    const base: Intl.NumberFormatOptions = compact
      ? { notation: "compact", maximumFractionDigits: 1 }
      : { maximumFractionDigits: 2 };
    try {
      format =
        column.format === "currency" && column.unit
          ? new Intl.NumberFormat(undefined, { ...base, style: "currency", currency: column.unit })
          : new Intl.NumberFormat(undefined, base);
    } catch {
      // Not a currency code the browser knows: the unit is shown as a suffix instead.
      format = new Intl.NumberFormat(undefined, base);
    }
    formatters.set(key, format);
  }
  return format;
}

/** A cell as shown: numbers in the column's format, compact for chart axes. */
export function formatCell(value: Cell, column: VisualColumn, compact = false): string {
  if (value === null) return "–";
  if (typeof value === "string") return value;
  if (column.format === "text") return String(value);
  const format = numberFormat(column, compact);
  const shown = format.format(value);
  if (column.format === "percent") return `${shown}%`;
  const currencyApplied = format.resolvedOptions().style === "currency";
  return column.unit && !currencyApplied ? `${shown} ${column.unit}` : shown;
}

/** Rows sorted by a column: numbers by value, text by locale; blanks last either way. */
export function sortRows(rows: Cell[][], column: number, direction: "asc" | "desc"): Cell[][] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[column] ?? null;
    const y = b[column] ?? null;
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign;
  });
}

function csvField(value: Cell): string {
  if (value === null) return "";
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

/** The visual's data as CSV, raw numbers (not formatted) so a spreadsheet can use them. */
export function toCsv(visual: Visual): string {
  const lines = [
    visual.columns.map((c) => csvField(c.label)),
    ...visual.rows.map((r) => r.map(csvField)),
  ];
  return `${lines.map((l) => l.join(",")).join("\r\n")}\r\n`;
}

/** Tab-separated, which pastes into a spreadsheet as cells and reads fine as text. */
export function toTsv(visual: Visual): string {
  const clean = (v: Cell) => (v === null ? "" : String(v).replace(/[\t\n\r]+/g, " "));
  return [visual.columns.map((c) => c.label), ...visual.rows]
    .map((r) => r.map((v) => clean(v as Cell)).join("\t"))
    .join("\n");
}

/** A file name for the CSV, from the title. */
export function csvFileName(visual: Visual): string {
  const base = (visual.title ?? visual.columns[1]?.label ?? "data")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return `${base || "data"}.csv`;
}

/** The numeric series of a chart: every column after the first, with its values per row. */
export function chartSeries(
  visual: Visual,
): { column: VisualColumn; index: number; values: (number | null)[] }[] {
  return visual.columns.slice(1).map((column, i) => ({
    column,
    index: i + 1,
    values: visual.rows.map((row) =>
      typeof row[i + 1] === "number" ? (row[i + 1] as number) : null,
    ),
  }));
}

/**
 * Round axis ticks from 0 (or the lowest negative value) to just past the highest value: 0, 25,
 * 50, 75, 100 rather than 0, 23.4, 46.8.
 */
export function niceTicks(values: number[], count = 4): number[] {
  const finite = values.filter(Number.isFinite);
  const max = Math.max(0, ...finite);
  const min = Math.min(0, ...finite);
  if (max === min) return [0, 1];
  const rough = (max - min) / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough;
  const ticks: number[] = [];
  for (let t = Math.floor(min / step) * step; t < max + step / 2; t += step) {
    ticks.push(Number(t.toPrecision(12)));
  }
  if ((ticks.at(-1) ?? 0) < max) ticks.push(Number(((ticks.at(-1) ?? 0) + step).toPrecision(12)));
  return ticks;
}

/** Series colors from Kourai's chart tokens: ink, red, blue, then grey, yellow and a light blue. */
const SERIES_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-5)",
  "var(--chart-4)",
  "color-mix(in oklch, var(--chart-3) 45%, var(--background))",
];

export const seriesColor = (i: number) => SERIES_COLORS[i % SERIES_COLORS.length] as string;
